"use client";

import { DirectionalChevron } from "@/components/core/DirectionalChevron";
import { Link, usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

interface LinkItemProps {
  href: string;
  icon: React.ReactNode;
  label: string;
  className?: string;
  disabled?: boolean;
  desktopOnly?: boolean;
  onClick?: () => void;
}

export const LinkItem = ({
  href,
  icon,
  label,
  className,
  disabled = false,
  desktopOnly = false,
  onClick,
}: LinkItemProps) => {
  const pathname = usePathname();
  const isActive = pathname.startsWith(href);

  const baseClassName = cn(
    "w-full flex items-center justify-between p-6",
    "transition-colors duration-200",
    !disabled && "hover:bg-bg-tertiary/40",
    "border-b border-stroke-subtle",
    disabled && "text-text-disabled",
    isActive &&
      !disabled &&
      "bg-bg-tertiary/40 text-text-brand font-medium border-l-2 border-l-text-brand",
    desktopOnly && "hidden md:flex",
    className,
  );

  if (disabled) {
    return (
      <div className={baseClassName}>
        <div className="flex items-center gap-2">
          <div className={cn(disabled && "text-text-disabled")}>{icon}</div>
          <p
            className={cn(
              "text-sm font-normal",
              disabled && "text-text-disabled",
            )}
          >
            {label}
          </p>
        </div>
        <DirectionalChevron
          size={16}
          className={cn(disabled && "text-text-disabled")}
        />
      </div>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(baseClassName, "cursor-pointer")}
      >
        <div className="flex items-center gap-2">
          <div className={cn(isActive && "text-text-brand")}>{icon}</div>
          <p className="text-sm font-normal">{label}</p>
        </div>
        <DirectionalChevron
          size={16}
          className={cn(isActive && "text-text-brand")}
        />
      </button>
    );
  }

  return (
    <Link href={href} className={baseClassName}>
      <div className="flex items-center gap-2">
        <div className={cn(isActive && "text-text-brand")}>{icon}</div>
        <p className="text-sm font-normal">{label}</p>
      </div>
      <DirectionalChevron
        size={16}
        className={cn(isActive && "text-text-brand")}
      />
    </Link>
  );
};
